class BdoCinematic extends HTMLElement {
  constructor() {
    super();
    this.frame = 0;
    this.inView = false;
    this.started = false;
    this.ready = false;
    this.pendingTime = 0;
    this.autoPlaying = false;
    this.complete = false;
    this.fallback = false;
    this.soundEnabled = false;
    this.lockedScrollY = 0;
    this.previousBodyTop = '';
    this.previousBodyPaddingRight = '';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileMedia = window.matchMedia('(max-width: 900px)');
    this.tick = this.tick.bind(this);
    this.markReady = this.markReady.bind(this);
    this.updateLoader = this.updateLoader.bind(this);
    this.updatePoster = this.updatePoster.bind(this);
    this.handleSeeked = this.handleSeeked.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
    this.handleSoundToggle = this.handleSoundToggle.bind(this);
    this.touchStartY = null;
    this.autoPlayStartedAt = 0;
    this.stallTimer = 0;
    this.capTimer = 0;
    this.notReadyTimer = 0;
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleWaiting = this.handleWaiting.bind(this);
    this.handlePlaying = this.handlePlaying.bind(this);
  }

  connectedCallback() {
    this.video = this.querySelector('[data-bdo-video]');
    this.whiteout = this.querySelector('[data-bdo-whiteout]');
    // Not `cue`: Safari defines a read-only `cue` property on every element.
    this.scrollCue = this.querySelector('[data-bdo-scroll-cue]');
    this.loader = this.querySelector('[data-bdo-loader]');
    this.loaderBar = this.querySelector('[data-bdo-loader-bar]');
    this.error = this.querySelector('[data-bdo-film-error]');
    this.skipLink = this.querySelector('[data-bdo-skip-film]');
    this.soundToggle = this.querySelector('[data-bdo-sound-toggle]');
    this.soundLabel = this.querySelector('[data-bdo-sound-label]');
    this.playbackTrigger = Math.min(Math.max(parseFloat(this.dataset.playbackTrigger) || 0.15, 0.05), 0.4);
    this.releaseDistance = 0.18;

    if (!this.video || !this.whiteout || !this.scrollCue) return;

    const shouldPlayThisSession = window.__bdoCinematicShouldPlay === true;
    window.__bdoCinematicShouldPlay = false;

    if (!shouldPlayThisSession) {
      this.dismissForSession();
      return;
    }

    this.updateSource();
    this.updatePoster();
    this.mobileMedia.addEventListener?.('change', this.updatePoster);
    this.video.addEventListener('loadedmetadata', this.markReady, { once: true });
    this.video.addEventListener('canplay', this.markReady, { once: true });
    this.video.addEventListener('progress', this.updateLoader);
    this.video.addEventListener('seeked', this.handleSeeked);
    this.video.addEventListener('error', this.handleError);
    this.video.addEventListener('ended', this.handleEnded);
    this.video.addEventListener('waiting', this.handleWaiting);
    this.video.addEventListener('playing', this.handlePlaying);
    window.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    window.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    this.skipLink?.addEventListener('click', this.handleSkip);
    this.soundToggle?.addEventListener('click', this.handleSoundToggle);

    if (this.video.readyState >= 1) this.markReady();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.startLoop();
        else if (!this.autoPlaying) this.stopLoop();
      },
      { threshold: 0 }
    );
    this.observer.observe(this);
  }

  dismissForSession() {
    this.complete = true;
    this.autoPlaying = false;
    this.video?.pause();
    this.classList.remove('is-auto-playing');
    this.classList.add('is-playback-complete', 'is-dismissed');
    this.style.height = '0';
    this.style.minHeight = '0';
    this.whiteout.style.opacity = '1';
    this.scrollCue.style.opacity = '0';
    if (this.loader) this.loader.hidden = true;
    if (this.soundToggle) this.soundToggle.disabled = true;
    if (this.skipLink) this.skipLink.hidden = true;
    document.documentElement.classList.add('bdo-store-active');
  }

  disconnectedCallback() {
    this.stopLoop();
    this.observer?.disconnect();
    this.mobileMedia.removeEventListener?.('change', this.updatePoster);
    this.video?.removeEventListener('progress', this.updateLoader);
    this.video?.removeEventListener('seeked', this.handleSeeked);
    this.video?.removeEventListener('error', this.handleError);
    this.video?.removeEventListener('ended', this.handleEnded);
    this.video?.removeEventListener('waiting', this.handleWaiting);
    this.video?.removeEventListener('playing', this.handlePlaying);
    window.removeEventListener('touchstart', this.handleTouchStart);
    window.removeEventListener('touchmove', this.handleTouchMove);
    this.clearHoldTimers();
    this.skipLink?.removeEventListener('click', this.handleSkip);
    this.soundToggle?.removeEventListener('click', this.handleSoundToggle);
    this.unlockPage();
  }

  // Phones get the vertical film, wider screens the landscape one. Chosen here, once, because
  // browsers disagree about <source media> inside <video>.
  updateSource() {
    const wanted = this.mobileMedia.matches ? this.dataset.mobileVideo : this.dataset.desktopVideo;
    if (!wanted || this.video.getAttribute('src') === wanted) return;
    this.video.src = wanted;
    this.video.load();
  }

  updatePoster() {
    if (!this.video) return;
    const poster = this.mobileMedia.matches ? this.dataset.mobilePoster : this.dataset.desktopPoster;
    if (poster) this.video.poster = poster;
  }

  updateLoader() {
    if (!this.video?.duration || !this.video.buffered.length || !this.loaderBar) return;
    const buffered = this.video.buffered.end(this.video.buffered.length - 1);
    const percent = Math.min(Math.max((buffered / this.video.duration) * 100, 8), 100);
    this.loaderBar.style.width = `${percent}%`;
  }

  markReady() {
    if (!this.video) return;
    const firstSignal = !this.ready;
    this.ready = true;
    this.classList.add('is-ready');
    if (this.loaderBar) this.loaderBar.style.width = '100%';
    if (firstSignal) {
      if (this.soundToggle) this.soundToggle.disabled = false;
      this.video.pause();
      if (!this.started) this.video.currentTime = 0;
    }
  }

  handleSeeked() {
    if (!this.started || this.autoPlaying || this.complete || this.reducedMotion.matches) return;
    if (Math.abs(this.pendingTime - this.video.currentTime) > 0.03) {
      this.video.currentTime = this.pendingTime;
    }
  }

  handleError() {
    this.autoPlaying = false;
    this.classList.remove('is-auto-playing');
    this.style.height = '118dvh';
    this.style.minHeight = '0';
    this.unlockPage();
    if (this.loader) this.loader.hidden = true;
    if (this.soundToggle) this.soundToggle.disabled = true;
    if (this.error) this.error.hidden = false;
  }

  handleEnded() {
    this.finishPlayback();
  }

  handleSkip(event) {
    event.preventDefault();
    if (!document.body.classList.contains('bdo-film-playing')) {
      this.lockedScrollY = window.scrollY;
    }
    this.finishPlayback({ enterStore: true });
  }

  // The page is held while the film plays. A held page must never trap a visitor:
  // a new swipe up enters the store, and a film that stalls or overruns releases it.
  handleTouchStart(event) {
    const settled = performance.now() - this.autoPlayStartedAt > 400;
    this.touchStartY = this.autoPlaying && settled ? event.touches[0].clientY : null;
  }

  handleTouchMove(event) {
    if (!this.autoPlaying || this.touchStartY === null) return;
    if (this.touchStartY - event.touches[0].clientY > 48) this.finishPlayback({ enterStore: true });
  }

  handleWaiting() {
    if (!this.autoPlaying || this.stallTimer) return;
    this.stallTimer = window.setTimeout(() => {
      this.stallTimer = 0;
      if (this.autoPlaying && !this.complete) this.finishPlayback({ enterStore: true });
    }, 3000);
  }

  handlePlaying() {
    window.clearTimeout(this.stallTimer);
    this.stallTimer = 0;
  }

  clearHoldTimers() {
    window.clearTimeout(this.stallTimer);
    window.clearTimeout(this.capTimer);
    window.clearTimeout(this.notReadyTimer);
    this.stallTimer = 0;
    this.capTimer = 0;
  }

  handleSoundToggle() {
    this.setFilmSound(!this.soundEnabled);
  }

  updateSoundToggle() {
    if (!this.soundToggle) return;
    this.soundToggle.setAttribute('aria-pressed', String(this.soundEnabled));
    this.soundToggle.setAttribute('aria-label', this.soundEnabled ? 'Turn film sound off' : 'Turn film sound on');
    if (this.soundLabel) this.soundLabel.textContent = 'Sound';
  }

  async setFilmSound(shouldEnable) {
    if (!this.ready || this.complete) return;

    this.soundEnabled = shouldEnable;
    this.video.muted = !this.soundEnabled;
    this.video.volume = 1;
    this.updateSoundToggle();

    if (!this.soundEnabled || this.autoPlaying || !this.video.paused) return;

    const resumeTime = this.video.currentTime;

    try {
      await this.video.play();
      this.video.pause();
      if (Math.abs(this.video.currentTime - resumeTime) > 0.025) {
        this.video.currentTime = resumeTime;
      }
    } catch {
      this.soundEnabled = false;
      this.video.muted = true;
      this.updateSoundToggle();
    }
  }

  lockPage() {
    if (document.body.classList.contains('bdo-film-playing')) return;

    this.lockedScrollY = window.scrollY;
    this.previousBodyTop = document.body.style.top;
    this.previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.top = `-${this.lockedScrollY}px`;
    if (scrollbarGap > 0) document.body.style.paddingRight = `${scrollbarGap}px`;
    document.body.classList.add('bdo-film-playing');
  }

  unlockPage() {
    if (!document.body.classList.contains('bdo-film-playing')) return;

    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.classList.remove('bdo-film-playing');
    document.body.style.top = this.previousBodyTop;
    document.body.style.paddingRight = this.previousBodyPaddingRight;
    window.scrollTo(0, this.lockedScrollY);
    document.documentElement.style.scrollBehavior = previousScrollBehavior;
  }

  videoProgress() {
    if (!this.video?.duration) return 0;
    return Math.min(Math.max(this.video.currentTime / this.video.duration, 0), 1);
  }

  updatePlaybackVisuals() {
    const fadeProgress = Math.min(Math.max((this.videoProgress() - 0.965) / 0.035, 0), 1);
    this.whiteout.style.opacity = fadeProgress.toFixed(3);
    this.scrollCue.style.opacity = '0';
  }

  async startAutoPlayback() {
    if (
      !this.ready ||
      this.autoPlaying ||
      this.complete ||
      this.fallback ||
      this.reducedMotion.matches
    ) {
      return;
    }

    this.autoPlaying = true;
    this.autoPlayStartedAt = performance.now();
    this.classList.add('is-auto-playing');
    this.lockPage();
    const remaining = Math.max((this.video.duration || 0) - this.video.currentTime, 0);
    this.capTimer = window.setTimeout(() => {
      if (this.autoPlaying && !this.complete) this.finishPlayback({ enterStore: true });
    }, (remaining + 4) * 1000);

    try {
      await this.video.play();
      this.startLoop();
    } catch {
      if (this.soundEnabled) {
        this.soundEnabled = false;
        this.video.muted = true;
        this.updateSoundToggle();

        try {
          await this.video.play();
          this.startLoop();
          return;
        } catch {
          this.video.pause();
        }
      }

      // Autoplay refused (Low Power Mode, in-app browsers). Scrubbing a film that never
      // loaded shows a frozen frame for five screens of scrolling, so open the store instead.
      this.finishPlayback({ enterStore: true });
    }
  }

  finishPlayback({ enterStore = true } = {}) {
    this.clearHoldTimers();
    if (!this.complete) {
      this.complete = true;
      this.autoPlaying = false;
      this.video?.pause();
      this.classList.remove('is-auto-playing');
      this.classList.add('is-playback-complete');
      this.whiteout.style.opacity = '1';
      this.scrollCue.style.opacity = '0';

      const progressOffset = Math.max(this.lockedScrollY - this.offsetTop, 0);
      const releasePixels = enterStore ? 0 : window.innerHeight * this.releaseDistance;
      this.style.height = `${Math.ceil(progressOffset + window.innerHeight + releasePixels)}px`;
      this.style.minHeight = '0';
    }

    window.requestAnimationFrame(() => {
      this.unlockPage();

      if (enterStore) {
        window.requestAnimationFrame(() => {
          const previousScrollBehavior = document.documentElement.style.scrollBehavior;
          document.documentElement.style.scrollBehavior = 'auto';
          document.documentElement.classList.add('bdo-store-active');
          if (this.skipLink) this.skipLink.hidden = true;
          this.classList.add('is-dismissed');
          this.style.height = '0';
          this.style.minHeight = '0';
          window.scrollTo(0, 0);
          window.requestAnimationFrame(() => {
            window.scrollTo(0, 0);
            window.setTimeout(() => {
              window.scrollTo(0, 0);
              document.documentElement.style.scrollBehavior = previousScrollBehavior;
            }, 80);
          });
        });
      }
    });
  }

  startLoop() {
    if (!this.frame) this.frame = window.requestAnimationFrame(this.tick);
  }

  stopLoop() {
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  tick() {
    this.frame = 0;
    if ((!this.inView && !this.autoPlaying) || this.complete) return;

    if (this.autoPlaying) {
      this.updatePlaybackVisuals();
      this.frame = window.requestAnimationFrame(this.tick);
      return;
    }

    const rect = this.getBoundingClientRect();
    const scrollRange = Math.max(this.offsetHeight - window.innerHeight, 1);
    const progress = Math.min(Math.max(-rect.top / scrollRange, 0), 1);

    if (progress > 0.0005) {
      this.started = true;
      this.classList.add('has-scroll-started');
    }

    if (!this.ready && !this.notReadyTimer && progress >= this.playbackTrigger) {
      this.video.load();
      this.notReadyTimer = window.setTimeout(() => {
        if (!this.ready && !this.complete) this.finishPlayback({ enterStore: true });
      }, 2000);
    }

    if (this.started && this.ready && !this.reducedMotion.matches) {
      const scrubProgress = this.fallback ? progress : Math.min(progress, this.playbackTrigger);
      this.pendingTime = scrubProgress * Math.max(this.video.duration - 0.04, 0);
      const difference = this.pendingTime - this.video.currentTime;
      if (!this.video.seeking && Math.abs(difference) > 0.025) {
        this.video.currentTime = this.pendingTime;
      }

      if (!this.fallback && progress >= this.playbackTrigger) {
        this.startAutoPlayback();
      }
    }

    const fadeProgress = this.fallback
      ? Math.min(Math.max((progress - 0.965) / 0.035, 0), 1)
      : 0;
    this.whiteout.style.opacity = fadeProgress.toFixed(3);

    const cueOpacity = this.ready
      ? Math.min(Math.max(1 - progress / this.playbackTrigger, 0), 1)
      : 0;
    this.scrollCue.style.opacity = cueOpacity.toFixed(3);

    if ((this.fallback || this.reducedMotion.matches) && progress >= 0.999) {
      this.finishPlayback({ enterStore: true });
      return;
    }

    this.frame = window.requestAnimationFrame(this.tick);
  }
}

if (!customElements.get('bdo-cinematic')) {
  customElements.define('bdo-cinematic', BdoCinematic);
}

class BdoProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-bdo-product-track]');
    this.previous = this.querySelector('[data-bdo-carousel-previous]');
    this.next = this.querySelector('[data-bdo-carousel-next]');
    if (!this.track || !this.previous || !this.next) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.previous.addEventListener('click', () => this.move(-1));
    this.next.addEventListener('click', () => this.move(1));
    this.track.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.move(event.key === 'ArrowLeft' ? -1 : 1);
    });
  }

  cardStep() {
    const card = this.track.querySelector('.bdo-product-card');
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  move(direction) {
    this.track.scrollBy({
      left: this.cardStep() * direction,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('bdo-product-carousel')) {
  customElements.define('bdo-product-carousel', BdoProductCarousel);
}

function initializeBdoReveal(root = document) {
  const items = [...root.querySelectorAll('.bdo-reveal:not(.is-visible)')];
  if (!items.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8%', threshold: 0.12 }
  );

  items.forEach((item, index) => {
    item.style.transitionDelay = `${Math.min(index % 3, 2) * 70}ms`;
    observer.observe(item);
  });
}

function initializeBdoMenus(root = document) {
  root.querySelectorAll('[data-bdo-collection-menu]').forEach((details) => {
    if (details.dataset.initialized === 'true') return;
    details.dataset.initialized = 'true';
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    details.addEventListener('mouseenter', () => {
      if (canHover.matches) details.open = true;
    });

    details.addEventListener('mouseleave', () => {
      if (canHover.matches && !details.matches(':focus-within')) details.open = false;
    });

    details.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      details.removeAttribute('open');
      details.querySelector('summary')?.focus();
    });

    details.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => details.removeAttribute('open'));
    });
  });
}

function initializeBdo(root = document) {
  initializeBdoReveal(root);
  initializeBdoMenus(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeBdo(), { once: true });
} else {
  initializeBdo();
}

document.addEventListener('shopify:section:load', (event) => initializeBdo(event.target));
